import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskedLines, Chapter } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";
import { useCart } from "@/context/CartContext";

const FEATURED = ["pumpkin-spice", "caramel-pumpkin-swirl", "cinnamon-orange", "toasted-marshmallow", "cashmere-pumpkin", "apple-pie"];
const FALL = ["pumpkin-spice", "caramel-pumpkin-swirl", "cinnamon-orange", "toasted-marshmallow"];
const OCCASIONS = ["Birthday Parties", "Girls' Nights", "Bridal Showers", "Baby Showers", "Church Events", "Women's Ministry Events", "Corporate Events", "Private Parties", "Special Celebrations"];
const FAMILIES = ["Vanilla / Sweet", "Floral", "Fresh / Clean", "Fruity", "Tropical", "Coconut", "Woody", "Warm / Cozy", "Citrus", "Masculine"];

const Home = () => {
  const { products } = useCart();
  const featured = FEATURED.map((s) => products.find((p) => p.slug === s)).filter(Boolean);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden bg-ivory">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20 pt-16 md:pt-24 pb-10 text-center">
          <motion.p data-testid="hero-subheadline" className="eyebrow"
            initial={{ opacity: 0, letterSpacing: "0.6em" }} animate={{ opacity: 1, letterSpacing: "0.35em" }} transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}>
            Hand-Poured Soy Wax Candles
          </motion.p>
          <div className="mt-10 overflow-hidden">
            <motion.img src="/assets/logo.png" alt="Merari Candle Company" data-testid="hero-logo"
              className="mx-auto w-full max-w-xl md:max-w-3xl object-contain"
              initial={{ y: "60%", opacity: 0, scale: 0.96 }} animate={{ y: 0, opacity: 1, scale: 1 }}
              transition={{ duration: 1.4, delay: 0.25, ease: [0.22, 1, 0.36, 1] }} />
          </div>
          <MaskedLines data-testid="hero-tagline"
            lines={["Beautifully Scented. Hand-Poured.", "Made With Intention."]}
            className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight"
            lineClassName="italic" delay={0.7} />
          <motion.div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.3 }}>
            <Link to="/shop" data-testid="shop-candles-button" className="btn-primary w-full sm:w-auto">Shop Candles</Link>
            <Link to="/create-your-scent" data-testid="create-your-scent-button" className="btn-outline w-full sm:w-auto">Create Your Scent</Link>
          </motion.div>
        </div>
        <div className="relative mx-auto max-w-5xl px-6 pb-24">
          <div className="pointer-events-none absolute left-1/2 top-1/2 h-[70%] w-[80%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-ink/[0.05] blur-3xl animate-flicker" />
          <motion.div style={{ y: imgY }} className="relative border border-line bg-white p-3 shadow-[0_40px_80px_-40px_rgba(10,10,10,0.25)]">
            <motion.div style={{ scale: imgScale }} className="overflow-hidden">
              <img src="/assets/pumpkin-spice.png" alt="Merari Pumpkin Spice fall soy candle in amber glass" data-testid="hero-candle-image" className="h-[320px] md:h-[460px] w-full object-cover" />
            </motion.div>
          </motion.div>
          <p className="mt-6 text-center font-mono text-[10px] uppercase tracking-[0.35em] text-stone2">The Fall Collection — Pumpkin Spice · Soy Candle · 9 oz</p>
        </div>
      </section>

      <Marquee />

      {/* NEW FALL COLLECTION */}
      <section className="border-b border-line bg-white py-24 md:py-32" data-testid="fall-collection-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
          <Reveal className="text-center">
            <p className="eyebrow">Just Poured</p>
            <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight">The New <em>Fall Collection</em></h2>
            <p className="mx-auto mt-6 max-w-xl text-sm md:text-base leading-relaxed text-stone2">
              Four autumn scents in our new amber glass vessel — pumpkin, caramel, cinnamon, and fireside marshmallow, hand-poured for the season.
            </p>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {FALL.map((slug, i) => {
              const p = products.find((x) => x.slug === slug);
              if (!p) return null;
              return (
                <Reveal key={slug} delay={i * 0.1}>
                  <Link to={`/product/${slug}`} data-testid={`fall-candle-${slug}`} className="group block">
                    <div className="overflow-hidden border border-line">
                      <img src={p.image} alt={`${p.name} fall soy candle`} className="aspect-square w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.05]" />
                    </div>
                    <p className="mt-3 text-center font-serif text-lg md:text-xl font-light tracking-tight group-hover:italic transition-all duration-300">{p.name}</p>
                  </Link>
                </Reveal>
              );
            })}
          </div>
          <Reveal className="mt-12 text-center">
            <Link to="/shop?collection=Seasonal Collection" data-testid="shop-fall-collection-button" className="btn-primary">Shop Fall Scents</Link>
          </Reveal>
        </div>
      </section>

      {/* WHY SOY */}
      <section className="bg-ivory py-24 md:py-36" data-testid="why-soy-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
          <Reveal>
            <p className="eyebrow">Why Soy Wax</p>
            <h2 className="mt-5 max-w-2xl font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight">The Beauty of Soy</h2>
            <p className="mt-6 max-w-xl text-sm md:text-base leading-relaxed text-stone2">
              Every Merari candle is thoughtfully hand-poured using soy wax to create a beautiful candle experience — from the first light to the final glow.
            </p>
          </Reveal>
          <div className="mt-16">
            <Chapter number="01" title="Soy Wax">A smooth, creamy wax that holds fragrance beautifully and gives every candle its clean, elegant finish.</Chapter>
            <Chapter number="02" title="Hand-Poured in Small Batches">Each candle is poured by hand in Manayunk — never mass-produced — so every pour receives our full attention.</Chapter>
            <Chapter number="03" title="Beautiful Fragrance">Scents are chosen and blended with care, designed to fill a room gently and linger long after the flame is out.</Chapter>
            <Chapter number="04" title="Carefully Crafted">From wick placement to the final label, every detail is considered, checked, and finished by hand.</Chapter>
            <Chapter number="05" title="Made With Intention">We make candles the slow way — thoughtfully, purposefully, and always with the moment they'll live in mind.</Chapter>
          </div>
        </div>
      </section>

      {/* FEATURED SCENTS */}
      <section className="border-t border-line bg-white py-24 md:py-32" data-testid="featured-scents-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="eyebrow">Featured Scents</p>
              <h2 className="mt-5 font-serif text-4xl sm:text-5xl font-light tracking-tight">Poured This Season</h2>
            </div>
            <Link to="/shop" data-testid="shop-all-candles-button" className="btn-outline">Shop All Candles</Link>
          </Reveal>
          <div className="mt-14 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
          </div>
        </div>
      </section>

      {/* EXPERIENCE TEASER */}
      <section className="bg-ink text-ivory py-24 md:py-36" data-testid="experience-teaser">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20 grid gap-16 lg:grid-cols-2 items-center">
          <Reveal>
            <p className="eyebrow !text-ivory/50">The Merari Candle Experience</p>
            <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]">
              We Bring the Candle-Making Experience <em>to You</em>
            </h2>
            <p className="mt-8 max-w-lg text-sm md:text-base leading-relaxed text-ivory/70">
              A mobile candle-making experience where your guests pour their own soy wax candles, select fragrances, blend scents, and leave with something beautiful they made themselves.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {OCCASIONS.map((o) => (
                <span key={o} className="border border-white/15 px-3.5 py-1.5 text-[10px] uppercase tracking-[0.2em] text-ivory/70">{o}</span>
              ))}
            </div>
            <Link to="/experience" data-testid="book-experience-button" className="btn-outline-light mt-10">Book a Candle Experience</Link>
          </Reveal>
          <Reveal delay={0.15} className="relative">
            <div className="border border-white/15 p-3">
              <img src="/assets/nutmeg-cedar.png" alt="Hand-poured Merari soy candle" className="h-[380px] md:h-[520px] w-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block border border-white/15 bg-ink px-8 py-6">
              <p className="font-serif text-3xl font-light italic">Pour. Blend. Take home.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CREATE YOUR SCENT TEASER */}
      <section className="bg-ivory py-24 md:py-36" data-testid="custom-scent-teaser">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20 text-center">
          <Reveal>
            <p className="eyebrow">Create Your Own Scent</p>
            <h2 className="mx-auto mt-5 max-w-3xl font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight">
              Your Candle. Your Fragrance. <em>Your Story.</em>
            </h2>
            <p className="mx-auto mt-8 max-w-xl text-sm md:text-base leading-relaxed text-stone2">
              Have a fragrance in mind? Tell us what you love and let Merari Candle Company help create a soy wax candle that's uniquely yours.
            </p>
            <div className="mx-auto mt-10 flex max-w-3xl flex-wrap justify-center gap-2">
              {FAMILIES.map((f) => (
                <span key={f} className="border border-line bg-white px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-stone2">{f}</span>
              ))}
            </div>
            <Link to="/create-your-scent" data-testid="teaser-create-scent-button" className="btn-primary mt-12">
              Create My Scent <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FAVORS TEASER */}
      <section className="border-y border-line bg-cream py-24 md:py-32" data-testid="favors-teaser">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20 grid gap-14 lg:grid-cols-2 items-center">
          <Reveal className="order-2 lg:order-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="border border-line bg-white p-2 mt-10"><img src="/assets/vanilla-cashmere.png" alt="Vanilla Cashmere soy candle favor" className="h-56 md:h-72 w-full object-cover" /></div>
              <div className="border border-line bg-white p-2"><img src="/assets/mango-coconut.png" alt="Mango Coconut soy candle favor" className="h-56 md:h-72 w-full object-cover" /></div>
            </div>
          </Reveal>
          <Reveal className="order-1 lg:order-2" delay={0.1}>
            <p className="eyebrow">Custom Candle Favors</p>
            <h2 className="mt-5 font-serif text-4xl sm:text-5xl font-light tracking-tight">Made Especially for Your Moment</h2>
            <p className="mt-8 max-w-lg text-sm md:text-base leading-relaxed text-stone2">
              Custom soy wax candle favors for weddings, showers, birthdays, church and women's events, corporate gatherings, and every occasion worth remembering — custom scents, custom labels, and bulk orders.
            </p>
            <Link to="/favors" data-testid="request-custom-favors-button" className="btn-primary mt-10">Request Custom Favors</Link>
          </Reveal>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="bg-ivory py-24 md:py-36" data-testid="about-teaser">
        <div className="mx-auto max-w-4xl px-6 text-center sm:px-12">
          <Reveal>
            <img src="/assets/logo.png" alt="Merari Candle Company" className="mx-auto h-14 w-auto object-contain" />
            <h2 className="mt-10 font-serif text-4xl sm:text-5xl font-light tracking-tight">More Than a Candle</h2>
            <p className="mx-auto mt-8 max-w-2xl font-serif text-xl md:text-2xl font-light italic leading-relaxed text-stone2">
              "Every candle is created with love, with the hope that it brings a little more comfort and beauty into your home."
            </p>
            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-stone2">Merari Torres · Founder</p>
            <Link to="/about" data-testid="about-read-more-link" className="btn-outline mt-12">Our Story</Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
